import type { GameEvent } from '../schema';

/**
 * Career events. The recurring theme is that advancement is never free: every
 * upward move here charges workload, time, or relationships, and the player is
 * rarely told the exact price in advance.
 */
export const CAREER_EVENTS: GameEvent[] = [
  {
    id: 'career.firstJob',
    title: 'The first offer',
    description:
      'Someone is willing to pay you to do something. It is not the job you imagined, but it starts on Monday.',
    category: 'career',
    pools: ['pool.career.entry'],
    stages: ['launch', 'earlyCareer'],
    weight: 10,
    requires: [{ k: 'careerStatus', anyOf: ['none', 'unemployed', 'student'] }],
    priority: 5,
    choices: [
      {
        id: 'take',
        label: 'Take it',
        detail: 'Start earning. Figure out the rest later.',
        hint: 'safe',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Started working',
            narrative: 'You are employed. The money is unremarkable and entirely yours.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'flag', flag: 'everEmployed', value: true },
              { k: 'stat', stat: 'purpose', delta: 5 },
              { k: 'memory', title: 'First job', tone: 'milestone', category: 'career', weight: 7 },
            ],
          },
        ],
      },
      {
        id: 'hold',
        label: 'Hold out for something better',
        detail: 'Keep looking. Rent does not pause for ambition.',
        hint: 'risky',
        tags: ['ambition', 'risk'],
        outcomes: [
          {
            weight: 40,
            label: 'A better offer came',
            narrative: 'Six weeks of nothing, then a genuinely good opening. Worth the wait.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'num', path: 'socialHealth', op: '>=', value: 60 }], multiplier: 1.5 },
              { conditions: [{ k: 'educationAtLeast', level: 'bachelor' }], multiplier: 1.4 },
            ],
            effects: [
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'career', op: 'adjust', field: 'performance', delta: 8 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 12 },
              { k: 'stat', stat: 'happiness', delta: 6 },
              { k: 'flag', flag: 'everEmployed', value: true },
            ],
          },
          {
            weight: 60,
            label: 'The gap stretched',
            narrative: 'Months passed. You took something worse than the original offer and pretended not to notice.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'cashIncomeMonths', months: -2, note: 'unemployment gap' },
              { k: 'stat', stat: 'stress', delta: 12 },
              { k: 'stat', stat: 'happiness', delta: -6 },
              { k: 'career', op: 'adjust', field: 'performance', delta: -4 },
              { k: 'flag', flag: 'everEmployed', value: true },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'career.promotionOffer',
    title: 'They want to promote you',
    description:
      'More scope, more money, and a calendar that will quietly colonise your evenings.',
    category: 'career',
    pools: ['pool.career.early', 'pool.career.mid'],
    weight: 8,
    cooldown: 3,
    requires: [
      { k: 'careerStatus', anyOf: ['employed'] },
      { k: 'num', path: 'performance', op: '>=', value: 52 },
      { k: 'num', path: 'roleLevel', op: '<', value: 4 },
    ],
    weightMods: [
      { conditions: [{ k: 'num', path: 'performance', op: '>=', value: 72 }], multiplier: 1.8 },
      { conditions: [{ k: 'trait', trait: 'ambition', op: '>=', value: 65 }], multiplier: 1.3 },
    ],
    choices: [
      {
        id: 'accept',
        label: 'Accept',
        detail: 'Take the title and the workload that comes stapled to it.',
        hint: 'mixed',
        tags: ['ambition', 'career'],
        outcomes: [
          {
            weight: 1,
            label: 'Promoted',
            narrative: 'New title, new salary, and a standing Monday meeting you will grow to resent.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'promote', levels: 1 },
              { k: 'career', op: 'adjust', field: 'workload', delta: 12 },
              { k: 'stat', stat: 'stress', delta: 8 },
              { k: 'stat', stat: 'purpose', delta: 4 },
              { k: 'burnout', op: 'load', delta: 6 },
              { k: 'aspiration', op: 'progress', defId: 'goal.seniorRole', delta: 40 },
              { k: 'memory', title: 'Promoted', tone: 'milestone', category: 'career', weight: 6 },
              {
                k: 'schedule',
                afterYears: 2,
                label: 'The cost of the promotion',
                requires: [{ k: 'num', path: 'workload', op: '>=', value: 70 }],
                narrative: 'Two years at this pace has left a mark.',
                effects: [
                  { k: 'burnout', op: 'load', delta: 12 },
                  { k: 'relationshipAll', dim: 'closeness', delta: -5 },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'negotiate',
        label: 'Accept — but negotiate the load',
        detail: 'Say yes to the scope, push back on the hours.',
        hint: 'mixed',
        requires: [{ k: 'skill', skill: 'communication', op: '>=', value: 40 }],
        showWhenLocked: true,
        tags: ['ambition', 'boundaries'],
        outcomes: [
          {
            weight: 55,
            label: 'They agreed',
            narrative: 'You asked for the headcount and got most of it. Rare, and largely down to how you asked.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'num', path: 'careerReputation', op: '>=', value: 60 }], multiplier: 1.6 },
              { conditions: [{ k: 'skill', skill: 'communication', op: '>=', value: 65 }], multiplier: 1.4 },
            ],
            effects: [
              { k: 'career', op: 'promote', levels: 1 },
              { k: 'career', op: 'adjust', field: 'workload', delta: 4 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: 6 },
              { k: 'stat', stat: 'purpose', delta: 6 },
              { k: 'aspiration', op: 'progress', defId: 'goal.seniorRole', delta: 40 },
            ],
          },
          {
            weight: 45,
            label: 'Noted, and ignored',
            narrative: 'They nodded warmly and changed nothing. You have the job and the hours.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'promote', levels: 1 },
              { k: 'career', op: 'adjust', field: 'workload', delta: 14 },
              { k: 'stat', stat: 'stress', delta: 10 },
              { k: 'burnout', op: 'load', delta: 8 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: -5 },
            ],
          },
        ],
      },
      {
        id: 'decline',
        label: 'Decline',
        detail: 'Stay where you are. Keep your evenings.',
        hint: 'safe',
        tags: ['boundaries', 'balance'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed put',
            narrative: 'You said no, kindly. It was noted — not held against you, exactly, but noted.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'adjust', field: 'reputation', delta: -4 },
              { k: 'stat', stat: 'stress', delta: -5 },
              { k: 'stat', stat: 'happiness', delta: 4 },
              { k: 'relationshipAll', kind: 'spouse', dim: 'closeness', delta: 5 },
              { k: 'counter', name: 'declinedPromotions', delta: 1 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'career.plateau',
    title: 'Nothing is moving',
    description:
      'Three years, same role, same work. You are good at it now, which is exactly the problem.',
    category: 'career',
    pools: ['pool.career.mid', 'pool.career.senior'],
    weight: 7,
    cooldown: 5,
    requires: [
      { k: 'careerStatus', anyOf: ['employed'] },
      { k: 'num', path: 'yearsExperience', op: '>=', value: 4 },
    ],
    weightMods: [{ conditions: [{ k: 'num', path: 'meaningAlignment', op: '<', value: 45 }], multiplier: 1.7 }],
    choices: [
      {
        id: 'push',
        label: 'Make a case for yourself',
        detail: 'Ask directly for the next step.',
        hint: 'mixed',
        tags: ['ambition'],
        outcomes: [
          {
            weight: 45,
            label: 'They moved',
            narrative: 'Turns out nobody had thought about it. Asking was the whole intervention.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'num', path: 'performance', op: '>=', value: 65 }], multiplier: 1.7 },
              { conditions: [{ k: 'skill', skill: 'communication', op: '>=', value: 55 }], multiplier: 1.4 },
            ],
            effects: [
              { k: 'career', op: 'promote', levels: 1 },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'trait', trait: 'socialConfidence', delta: 4 },
            ],
          },
          {
            weight: 55,
            label: 'A vague promise',
            narrative: '"Let’s revisit next cycle." You have heard this sentence before, in this chair.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'happiness', delta: -5 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: -8 },
              { k: 'intention', op: 'add', id: 'int.jobSearch', label: 'Start looking elsewhere', topic: 'career', urgency: 35, resumeEventId: 'career.jobSearch', decayEventId: 'career.staleCareer' },
            ],
          },
        ],
      },
      {
        id: 'look',
        label: 'Start looking elsewhere',
        detail: 'Quietly. On your own time.',
        hint: 'mixed',
        tags: ['change'],
        inertiaRisk: 0.3,
        outcomes: [
          {
            weight: 50,
            label: 'Found something better',
            narrative: 'A competitor wanted exactly your five years of experience, and paid for it.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'num', path: 'careerReputation', op: '>=', value: 55 }], multiplier: 1.7 },
              { conditions: [{ k: 'num', path: 'socialHealth', op: '>=', value: 62 }], multiplier: 1.35 },
            ],
            effects: [
              { k: 'salary', mode: 'mult', value: 1.22 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 10 },
              { k: 'career', op: 'adjust', field: 'jobSecurity', delta: -8 },
              { k: 'stat', stat: 'happiness', delta: 7 },
              { k: 'memory', title: 'Changed employers', tone: 'positive', category: 'career', weight: 4 },
            ],
          },
          {
            weight: 32,
            label: 'A lateral move',
            narrative: 'Same money, different logo. Fresh at least.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 5 },
              { k: 'career', op: 'adjust', field: 'jobSecurity', delta: -5 },
              { k: 'stat', stat: 'stress', delta: 4 },
            ],
          },
          {
            weight: 18,
            label: 'Nothing landed',
            narrative: 'Eleven applications, two interviews, no offers. You are still here.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'happiness', delta: -6 },
              { k: 'trait', trait: 'optimism', delta: -3 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: -5 },
            ],
          },
        ],
      },
      {
        id: 'accept',
        label: 'Accept it for now',
        detail: 'Coast. Put the energy somewhere else.',
        hint: 'safe',
        tags: ['balance'],
        defers: { id: 'int.careerMove', label: 'Do something about the career', topic: 'career', urgency: 30, resumeEventId: 'career.jobSearch', decayEventId: 'career.staleCareer' },
        outcomes: [
          {
            weight: 1,
            label: 'Coasted',
            narrative: 'You stopped pushing and redirected the energy. Some of that energy went nowhere at all.',
            tone: 'neutral',
            effects: [
              { k: 'stat', stat: 'stress', delta: -6 },
              { k: 'stat', stat: 'energy', delta: 5 },
              { k: 'career', op: 'adjust', field: 'performance', delta: -4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'career.jobSearch',
    title: 'You said you would look',
    description: 'The intention has been sitting there a while. The market does not wait indefinitely.',
    category: 'career',
    pools: ['pool.career.mid', 'pool.inertia'],
    weight: 6,
    requires: [{ k: 'intention', topic: 'career', present: true }],
    choices: [
      {
        id: 'act',
        label: 'Actually do it this time',
        hint: 'mixed',
        tags: ['change'],
        outcomes: [
          {
            weight: 58,
            label: 'Moved',
            narrative: 'Three weeks of evenings and an offer. The delay cost you nothing except the delay.',
            tone: 'positive',
            effects: [
              { k: 'intention', op: 'resolve', id: 'int.jobSearch' },
              { k: 'intention', op: 'resolve', id: 'int.careerMove' },
              { k: 'salary', mode: 'mult', value: 1.16 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 12 },
              { k: 'stat', stat: 'purpose', delta: 6 },
              { k: 'habit', habit: 'procrastination', delta: -6 },
            ],
          },
          {
            weight: 42,
            label: 'Harder than it used to be',
            narrative: 'You are more expensive and less novel than you were five years ago. It took a while.',
            tone: 'neutral',
            effects: [
              { k: 'intention', op: 'resolve', id: 'int.jobSearch' },
              { k: 'intention', op: 'resolve', id: 'int.careerMove' },
              { k: 'stat', stat: 'stress', delta: 8 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 4 },
            ],
          },
        ],
      },
      {
        id: 'later',
        label: 'Not this year',
        hint: 'safe',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'Deferred again',
            narrative: 'It went back on the list. The list is getting long.',
            tone: 'negative',
            effects: [
              { k: 'intention', op: 'defer', id: 'int.jobSearch' },
              { k: 'inertia', op: 'pressure', delta: 7 },
              { k: 'habit', habit: 'procrastination', delta: 4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'career.staleCareer',
    title: 'The window closed',
    description:
      'The move you kept meaning to make is no longer available. Your specialism has narrowed around you.',
    category: 'career',
    pools: ['pool.career.mid', 'pool.inertia'],
    weight: 0,
    choices: [
      {
        id: 'ack',
        label: 'Notice it',
        hint: 'unknown',
        outcomes: [
          {
            weight: 1,
            label: 'A door shut',
            narrative: 'Nobody announced it. You simply stopped being the kind of candidate you used to be.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: -10 },
              { k: 'regret', op: 'add', id: 'regret.careerMove', label: 'Never made the career move', weight: 20 },
              { k: 'stat', stat: 'purpose', delta: -6 },
              { k: 'memory', title: 'The career move that never happened', tone: 'negative', category: 'career', weight: 5 },
            ],
          },
        ],
      },
    ],
    automatic: true,
  },
  {
    id: 'career.layoffRound',
    title: 'Restructuring',
    description: 'A slide deck explains that the business must become simpler. Some of the simplification is you.',
    category: 'career',
    pools: ['pool.career.early', 'pool.career.mid', 'pool.career.senior', 'pool.crisis'],
    weight: 8,
    cooldown: 5,
    requires: [{ k: 'careerStatus', anyOf: ['employed'] }],
    weightMods: [
      { conditions: [{ k: 'str', path: 'marketRegime', op: '==', value: 'bust' }], multiplier: 3.2 },
      { conditions: [{ k: 'str', path: 'marketRegime', op: '==', value: 'soft' }], multiplier: 1.9 },
      { conditions: [{ k: 'num', path: 'jobSecurity', op: '<', value: 45 }], multiplier: 1.8 },
      { conditions: [{ k: 'num', path: 'performance', op: '>=', value: 70 }], multiplier: 0.5 },
    ],
    choices: [
      {
        id: 'face',
        label: 'Wait for the list',
        hint: 'unknown',
        outcomes: [
          {
            weight: 45,
            label: 'You survived',
            narrative: 'Your name was not on it. Two colleagues you liked were. The office is quieter.',
            tone: 'neutral',
            favouredBy: [
              { conditions: [{ k: 'num', path: 'performance', op: '>=', value: 65 }], multiplier: 2.1 },
              { conditions: [{ k: 'num', path: 'careerReputation', op: '>=', value: 60 }], multiplier: 1.6 },
            ],
            effects: [
              { k: 'stat', stat: 'stress', delta: 10 },
              { k: 'career', op: 'adjust', field: 'workload', delta: 10 },
              { k: 'career', op: 'adjust', field: 'jobSecurity', delta: -10 },
              { k: 'burnout', op: 'load', delta: 6 },
            ],
          },
          {
            weight: 55,
            label: 'Let go',
            narrative: 'A short meeting, a severance figure, and a laptop to return by Friday.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'layoff' },
              { k: 'cashIncomeMonths', months: 2.5 },
              { k: 'counter', name: 'crises', delta: 1 },
              { k: 'memory', title: 'Made redundant', tone: 'negative', category: 'career', weight: 8 },
              { k: 'triggerEvent', eventId: 'career.unemployed' },
            ],
          },
        ],
      },
      {
        id: 'volunteer',
        label: 'Volunteer for redundancy',
        detail: 'Take the package on your own terms.',
        hint: 'costly',
        tags: ['change', 'risk'],
        outcomes: [
          {
            weight: 1,
            label: 'Took the package',
            narrative: 'You chose the exit, which turns out to feel completely different from being chosen.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'setStatus', status: 'break' },
              { k: 'cashIncomeMonths', months: 4 },
              { k: 'stat', stat: 'stress', delta: -4 },
              { k: 'trait', trait: 'adaptability', delta: 5 },
              { k: 'memory', title: 'Took voluntary redundancy', tone: 'neutral', category: 'career', weight: 6 },
              { k: 'triggerEvent', eventId: 'career.unemployed', afterYears: 1 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'career.unemployed',
    title: 'Between things',
    description: 'No job, an unfamiliar amount of time, and a bank balance with a visible slope.',
    category: 'career',
    pools: ['pool.career.early', 'pool.career.mid'],
    weight: 9,
    requires: [{ k: 'careerStatus', anyOf: ['unemployed', 'break'] }],
    priority: 4,
    choices: [
      {
        id: 'grab',
        label: 'Take the first thing available',
        detail: 'Stop the bleeding.',
        hint: 'safe',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Back to work',
            narrative: 'A step sideways, possibly down. The direct deposit resumed, which was the point.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'career', op: 'demote', levels: 1 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: -8 },
              { k: 'stat', stat: 'stress', delta: -8 },
            ],
          },
        ],
      },
      {
        id: 'targeted',
        label: 'Search properly',
        detail: 'Take three months. Use the network.',
        hint: 'mixed',
        requires: [{ k: 'num', path: 'runwayMonths', op: '>=', value: 3 }],
        showWhenLocked: true,
        tags: ['patience'],
        outcomes: [
          {
            weight: 62,
            label: 'A good landing',
            narrative: 'Your old manager knew someone. It paid more than the job you lost.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'num', path: 'socialHealth', op: '>=', value: 60 }], multiplier: 1.8 },
              { conditions: [{ k: 'num', path: 'careerReputation', op: '>=', value: 60 }], multiplier: 1.7 },
            ],
            effects: [
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'salary', mode: 'mult', value: 1.08 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 8 },
              { k: 'cashIncomeMonths', months: -3, note: 'job search' },
            ],
          },
          {
            weight: 38,
            label: 'It took longer',
            narrative: 'Eight months. The savings took the hit so your CV would not have to.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'cashIncomeMonths', months: -7, note: 'long job search' },
              { k: 'stat', stat: 'stress', delta: 14 },
              { k: 'trait', trait: 'resilience', delta: 5 },
            ],
          },
        ],
      },
      {
        id: 'retrain',
        label: 'Use the gap to retrain',
        detail: 'Turn the setback into a pivot.',
        hint: 'slow',
        tags: ['change', 'growth'],
        outcomes: [
          {
            weight: 1,
            label: 'Started retraining',
            narrative: 'If you are going to be unemployed, be unemployed on purpose.',
            tone: 'neutral',
            effects: [
              { k: 'education', op: 'enroll', programId: 'edu.bootcamp' },
              { k: 'career', op: 'setStatus', status: 'student' },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'habit', habit: 'learning', delta: 10 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'career.mentor',
    title: 'Someone takes an interest',
    description:
      'A senior colleague starts giving you the kind of advice people usually charge for. It costs you nothing but attention.',
    category: 'career',
    pools: ['pool.career.early', 'pool.career.mid', 'pool.opportunity'],
    weight: 5,
    unique: true,
    requires: [{ k: 'careerStatus', anyOf: ['employed', 'selfEmployed'] }],
    weightMods: [{ conditions: [{ k: 'skill', skill: 'social', op: '>=', value: 50 }], multiplier: 1.5 }],
    choices: [
      {
        id: 'accept',
        label: 'Make time for it',
        hint: 'safe',
        tags: ['growth', 'social'],
        outcomes: [
          {
            weight: 1,
            label: 'Found a mentor',
            narrative: 'A standing coffee, once a month, that reroutes your next decade.',
            tone: 'positive',
            effects: [
              { k: 'person', op: 'add', kind: 'mentor', tags: ['career'], bond: { trust: 66, support: 62, closeness: 48 } },
              { k: 'career', op: 'adjust', field: 'performance', delta: 7 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: 6 },
              { k: 'skill', skill: 'communication', delta: 4 },
              {
                k: 'schedule',
                afterYears: 3,
                label: 'A mentor’s introduction',
                requires: [{ k: 'hasPerson', kind: 'mentor' }],
                eventId: 'opportunity.introduction',
                effects: [],
              },
            ],
          },
        ],
      },
      {
        id: 'decline',
        label: 'Politely stay busy',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Let it lapse',
            narrative: 'You were flattered and did nothing with it. They moved on to someone who replied.',
            tone: 'neutral',
            effects: [{ k: 'counter', name: 'missedOpportunities', delta: 1 }],
          },
        ],
      },
    ],
  },
  {
    id: 'career.toxicWorkplace',
    title: 'It has gone bad here',
    description:
      'New leadership, old resentments. The work is unchanged; being in the building has become the hard part.',
    category: 'career',
    pools: ['pool.career.mid', 'pool.stress'],
    weight: 5,
    cooldown: 8,
    requires: [{ k: 'careerStatus', anyOf: ['employed'] }, { k: 'num', path: 'yearsExperience', op: '>=', value: 3 }],
    choices: [
      {
        id: 'leave',
        label: 'Leave without a plan',
        detail: 'Protect yourself first.',
        hint: 'costly',
        tags: ['boundaries', 'risk'],
        outcomes: [
          {
            weight: 1,
            label: 'Walked out',
            narrative: 'You resigned on a Tuesday with nothing lined up, and slept properly that night.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'quit' },
              { k: 'stat', stat: 'stress', delta: -18 },
              { k: 'stat', stat: 'happiness', delta: 6 },
              { k: 'burnout', op: 'load', delta: -12 },
              { k: 'triggerEvent', eventId: 'career.unemployed' },
            ],
          },
        ],
      },
      {
        id: 'endure',
        label: 'Endure it',
        detail: 'Keep the salary. Absorb the cost.',
        hint: 'risky',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed',
            narrative: 'You became very good at leaving on time and feeling nothing in meetings.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'stress', delta: 16 },
              { k: 'burnout', op: 'load', delta: 16 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: -18 },
              { k: 'stat', stat: 'happiness', delta: -8 },
              {
                k: 'schedule',
                afterYears: 2,
                label: 'Two years of that',
                requires: [{ k: 'num', path: 'burnoutLoad', op: '>=', value: 45 }],
                effects: [{ k: 'burnout', op: 'load', delta: 14 }, { k: 'stat', stat: 'health', delta: -6 }],
              },
            ],
          },
        ],
      },
      {
        id: 'fight',
        label: 'Try to change it',
        detail: 'Raise it formally. Build allies.',
        hint: 'risky',
        requires: [{ k: 'skill', skill: 'communication', op: '>=', value: 48 }],
        showWhenLocked: true,
        tags: ['courage'],
        outcomes: [
          {
            weight: 35,
            label: 'It worked',
            narrative: 'The complaint landed. Someone senior left instead of you, and people noticed who raised it.',
            tone: 'positive',
            favouredBy: [{ conditions: [{ k: 'num', path: 'careerReputation', op: '>=', value: 62 }], multiplier: 2 }],
            effects: [
              { k: 'career', op: 'adjust', field: 'reputation', delta: 12 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 10 },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'trait', trait: 'resilience', delta: 5 },
            ],
          },
          {
            weight: 65,
            label: 'You became the problem',
            narrative: 'Nothing changed except how you were described in rooms you were not in.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'adjust', field: 'jobSecurity', delta: -22 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: -8 },
              { k: 'stat', stat: 'stress', delta: 14 },
              { k: 'triggerEvent', eventId: 'career.layoffRound', afterYears: 1 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'career.pivotOffer',
    title: 'A different field entirely',
    description:
      'Someone thinks your experience transfers. It would mean starting lower and learning in public.',
    category: 'career',
    pools: ['pool.career.pivot', 'pool.opportunity'],
    weight: 4,
    cooldown: 10,
    requires: [
      { k: 'num', path: 'yearsExperience', op: '>=', value: 5 },
      { k: 'age', min: 28, max: 58 },
    ],
    weightMods: [
      { conditions: [{ k: 'num', path: 'meaningAlignment', op: '<', value: 42 }], multiplier: 2.2 },
      { conditions: [{ k: 'trait', trait: 'noveltySeeking', op: '>=', value: 62 }], multiplier: 1.5 },
    ],
    choices: [
      {
        id: 'pivot',
        label: 'Take the leap',
        detail: 'Lower title, lower pay, different life.',
        hint: 'risky',
        tags: ['change', 'risk', 'courage'],
        outcomes: [
          {
            weight: 60,
            label: 'It fit',
            narrative: 'Eighteen awkward months, then the realisation that you should have done this years ago.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'trait', trait: 'adaptability', op: '>=', value: 60 }], multiplier: 1.7 },
              { conditions: [{ k: 'num', path: 'runwayMonths', op: '>=', value: 6 }], multiplier: 1.5 },
            ],
            effects: [
              { k: 'counter', name: 'pivotSuccess', delta: 1 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 26 },
              { k: 'stat', stat: 'purpose', delta: 16 },
              { k: 'trait', trait: 'adaptability', delta: 6 },
              { k: 'salary', mode: 'mult', value: 0.82 },
              { k: 'memory', title: 'Changed fields', tone: 'milestone', category: 'career', weight: 8 },
              { k: 'schedule', afterYears: 4, label: 'The pivot pays off', effects: [{ k: 'salary', mode: 'mult', value: 1.35 }, { k: 'career', op: 'adjust', field: 'reputation', delta: 10 }] },
            ],
          },
          {
            weight: 40,
            label: 'A hard landing',
            narrative: 'The transferable skills were less transferable than advertised. Two lean years.',
            tone: 'negative',
            effects: [
              { k: 'salary', mode: 'mult', value: 0.68 },
              { k: 'stat', stat: 'stress', delta: 14 },
              { k: 'career', op: 'adjust', field: 'performance', delta: -8 },
              { k: 'trait', trait: 'resilience', delta: 6 },
              { k: 'counter', name: 'crises', delta: 1 },
            ],
          },
        ],
      },
      {
        id: 'decline',
        label: 'Stay on the track you know',
        hint: 'safe',
        defers: { id: 'int.pivot', label: 'Change fields', topic: 'career', urgency: 22, decayEventId: 'career.pivotGone' },
        outcomes: [
          {
            weight: 1,
            label: 'Stayed',
            narrative: 'The safe answer, and not obviously the wrong one. You thought about it for months.',
            tone: 'neutral',
            effects: [{ k: 'counter', name: 'missedOpportunities', delta: 1 }],
          },
        ],
      },
    ],
  },
  {
    id: 'career.pivotGone',
    title: 'Too late to start over',
    description: 'The industry you were curious about now hires people half your age for a third of your salary.',
    category: 'career',
    pools: ['pool.inertia'],
    weight: 0,
    automatic: true,
    choices: [
      {
        id: 'ack',
        label: 'Let it go',
        outcomes: [
          {
            weight: 1,
            label: 'Closed that door',
            narrative: 'You stopped reading about it, which was a kind of decision.',
            tone: 'negative',
            effects: [
              { k: 'regret', op: 'add', id: 'regret.pivot', label: 'Never changed fields', weight: 18 },
              { k: 'stat', stat: 'purpose', delta: -4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'career.sabbaticalOffer',
    title: 'Unpaid leave is available',
    description: 'Six months, no salary, job held open. Almost nobody takes it.',
    category: 'career',
    pools: ['pool.career.mid', 'pool.career.senior', 'pool.leisure'],
    weight: 4,
    cooldown: 12,
    requires: [
      { k: 'careerStatus', anyOf: ['employed'] },
      { k: 'num', path: 'yearsExperience', op: '>=', value: 7 },
    ],
    weightMods: [{ conditions: [{ k: 'num', path: 'burnoutLoad', op: '>=', value: 45 }], multiplier: 2.4 }],
    choices: [
      {
        id: 'take',
        label: 'Take the six months',
        hint: 'costly',
        requires: [{ k: 'num', path: 'runwayMonths', op: '>=', value: 8 }],
        showWhenLocked: true,
        tags: ['rest', 'balance'],
        cost: [{ k: 'cashIncomeMonths', months: -6, note: 'sabbatical' }],
        outcomes: [
          {
            weight: 1,
            label: 'Took the leave',
            narrative: 'You slept, walked, and remembered what you are like when nothing is urgent.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'setStatus', status: 'break' },
              { k: 'stat', stat: 'energy', delta: 26 },
              { k: 'stat', stat: 'stress', delta: -28 },
              { k: 'burnout', op: 'load', delta: -30 },
              { k: 'stat', stat: 'happiness', delta: 14 },
              { k: 'stat', stat: 'purpose', delta: 6 },
              { k: 'counter', name: 'travelTrips', delta: 1 },
              { k: 'memory', title: 'Took a sabbatical', tone: 'positive', category: 'wellbeing', weight: 6 },
              { k: 'schedule', afterYears: 1, label: 'Back to work', effects: [{ k: 'career', op: 'setStatus', status: 'employed' }, { k: 'career', op: 'adjust', field: 'reputation', delta: -4 }] },
            ],
          },
        ],
      },
      {
        id: 'skip',
        label: 'Not realistic',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Kept working',
            narrative: 'You did the arithmetic twice and closed the tab.',
            tone: 'neutral',
            effects: [{ k: 'burnout', op: 'load', delta: 4 }],
          },
        ],
      },
    ],
  },
  {
    id: 'career.mentorOthers',
    title: 'A junior needs someone',
    description: 'They are floundering the way you once floundered. Helping properly takes hours you do not have.',
    category: 'career',
    pools: ['pool.career.senior', 'pool.career.late', 'pool.legacy'],
    weight: 5,
    cooldown: 4,
    requires: [{ k: 'num', path: 'yearsExperience', op: '>=', value: 9 }],
    choices: [
      {
        id: 'help',
        label: 'Take them on properly',
        hint: 'safe',
        tags: ['contribution', 'social'],
        outcomes: [
          {
            weight: 1,
            label: 'Mentored someone',
            narrative: 'You gave them the map you had to draw yourself. It cost about two hours a month.',
            tone: 'positive',
            effects: [
              { k: 'counter', name: 'contributions', delta: 1 },
              { k: 'aspiration', op: 'progress', defId: 'dream.contribute', delta: 22 },
              { k: 'stat', stat: 'purpose', delta: 9 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: 5 },
              { k: 'career', op: 'adjust', field: 'workload', delta: 3 },
              { k: 'skill', skill: 'management', delta: 4 },
            ],
          },
        ],
      },
      {
        id: 'light',
        label: 'Answer questions when asked',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Helped a bit',
            narrative: 'Useful, forgettable, and appropriately bounded.',
            tone: 'neutral',
            effects: [{ k: 'career', op: 'adjust', field: 'reputation', delta: 2 }],
          },
        ],
      },
    ],
  },
  {
    id: 'career.overtimeCulture',
    title: 'Everyone is still online at nine',
    description: 'Nobody asked you to work late. Nobody has to.',
    category: 'career',
    pools: ['pool.career.early', 'pool.career.mid', 'pool.stress'],
    weight: 6,
    cooldown: 4,
    requires: [{ k: 'careerStatus', anyOf: ['employed'] }, { k: 'num', path: 'workload', op: '>=', value: 55 }],
    choices: [
      {
        id: 'match',
        label: 'Match the pace',
        hint: 'risky',
        tags: ['ambition'],
        outcomes: [
          {
            weight: 1,
            label: 'Kept up',
            narrative: 'You were visibly committed, which is a currency, and it bought you something real.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'adjust', field: 'performance', delta: 9 },
              { k: 'career', op: 'adjust', field: 'workload', delta: 9 },
              { k: 'burnout', op: 'load', delta: 11 },
              { k: 'stat', stat: 'energy', delta: -9 },
              { k: 'habit', habit: 'workaholism', delta: 8 },
              { k: 'relationshipAll', dim: 'closeness', delta: -3 },
            ],
          },
        ],
      },
      {
        id: 'boundary',
        label: 'Leave at six anyway',
        hint: 'mixed',
        tags: ['boundaries', 'balance'],
        outcomes: [
          {
            weight: 70,
            label: 'Nothing happened',
            narrative: 'The world did not end. You had dinner at a table.',
            tone: 'positive',
            favouredBy: [{ conditions: [{ k: 'num', path: 'performance', op: '>=', value: 62 }], multiplier: 1.8 }],
            effects: [
              { k: 'stat', stat: 'energy', delta: 7 },
              { k: 'stat', stat: 'stress', delta: -6 },
              { k: 'habit', habit: 'workaholism', delta: -6 },
              { k: 'relationshipAll', dim: 'closeness', delta: 4 },
            ],
          },
          {
            weight: 30,
            label: 'It was noticed',
            narrative: 'Your review used the word "availability" in a sentence that was not a compliment.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'adjust', field: 'performance', delta: -6 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: -5 },
              { k: 'stat', stat: 'energy', delta: 5 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'career.reputationWin',
    title: 'The work got noticed',
    description: 'Something you built is being used far outside the team that paid for it.',
    category: 'career',
    pools: ['pool.career.mid', 'pool.career.senior', 'pool.opportunity'],
    weight: 4,
    cooldown: 6,
    requires: [{ k: 'num', path: 'performance', op: '>=', value: 62 }],
    weightMods: [{ conditions: [{ k: 'skill', skill: 'creative', op: '>=', value: 60 }], multiplier: 1.4 }],
    choices: [
      {
        id: 'lean',
        label: 'Put your name on it publicly',
        hint: 'mixed',
        tags: ['ambition', 'visibility'],
        outcomes: [
          {
            weight: 74,
            label: 'Recognition',
            narrative: 'A talk, a write-up, and an inbox that now contains real options.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'adjust', field: 'reputation', delta: 14 },
              { k: 'counter', name: 'creativeWorks', delta: 1 },
              { k: 'stat', stat: 'purpose', delta: 7 },
              { k: 'aspiration', op: 'progress', defId: 'goal.mastery', delta: 25 },
              { k: 'schedule', afterYears: 2, label: 'Reputation opens a door', eventId: 'opportunity.headhunted', effects: [] },
            ],
          },
          {
            weight: 26,
            label: 'Resented',
            narrative: 'Credit is zero-sum in some rooms. Two colleagues stopped inviting you to things.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'adjust', field: 'reputation', delta: 6 },
              { k: 'relationshipAll', kind: 'colleague', dim: 'conflict', delta: 14 },
              { k: 'stat', stat: 'stress', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'quiet',
        label: 'Let the work speak',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Stayed quiet',
            narrative: 'Your manager knows. That may be the extent of it.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'adjust', field: 'reputation', delta: 4 },
              { k: 'career', op: 'adjust', field: 'performance', delta: 3 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'career.lateCareerChoice',
    title: 'Coast or contribute',
    description:
      'You could do this job with your eyes closed for another eight years. Or you could spend them differently.',
    category: 'career',
    pools: ['pool.career.late'],
    weight: 8,
    cooldown: 6,
    requires: [{ k: 'age', min: 55 }, { k: 'careerStatus', anyOf: ['employed', 'selfEmployed'] }],
    choices: [
      {
        id: 'coast',
        label: 'Coast to the finish',
        hint: 'safe',
        tags: ['balance'],
        outcomes: [
          {
            weight: 1,
            label: 'Coasted',
            narrative: 'Competent, present, unbothered. You started leaving at four.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'adjust', field: 'workload', delta: -14 },
              { k: 'stat', stat: 'stress', delta: -10 },
              { k: 'stat', stat: 'energy', delta: 8 },
              { k: 'career', op: 'adjust', field: 'performance', delta: -6 },
            ],
          },
        ],
      },
      {
        id: 'teach',
        label: 'Spend it building people',
        hint: 'safe',
        tags: ['contribution', 'legacy'],
        outcomes: [
          {
            weight: 1,
            label: 'Became the person who trains everyone',
            narrative: 'The output stopped being yours, which turned out to be the point.',
            tone: 'positive',
            effects: [
              { k: 'counter', name: 'contributions', delta: 2 },
              { k: 'aspiration', op: 'progress', defId: 'dream.contribute', delta: 32 },
              { k: 'stat', stat: 'purpose', delta: 14 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: 8 },
            ],
          },
        ],
      },
      {
        id: 'last-push',
        label: 'One more level',
        hint: 'risky',
        requires: [{ k: 'num', path: 'roleLevel', op: '<', value: 4 }],
        showWhenLocked: true,
        tags: ['ambition'],
        outcomes: [
          {
            weight: 45,
            label: 'Made it',
            narrative: 'The last title. It cost two years of weekends you will not get back.',
            tone: 'positive',
            favouredBy: [{ conditions: [{ k: 'num', path: 'careerReputation', op: '>=', value: 68 }], multiplier: 1.9 }],
            effects: [
              { k: 'career', op: 'promote', levels: 1 },
              { k: 'burnout', op: 'load', delta: 14 },
              { k: 'stat', stat: 'health', delta: -5 },
              { k: 'relationshipAll', dim: 'closeness', delta: -5 },
            ],
          },
          {
            weight: 55,
            label: 'Passed over',
            narrative: 'They gave it to someone younger and told you it was about "trajectory".',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'happiness', delta: -10 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: -12 },
              { k: 'burnout', op: 'load', delta: 10 },
              { k: 'regret', op: 'add', id: 'regret.lastPush', label: 'Chased one more promotion', weight: 12 },
            ],
          },
        ],
      },
    ],
  },
];
